import { lazy, Suspense, type ComponentType } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGate } from './components/AuthGate.js';
import { DeviceGate } from './components/DeviceGate.js';
import { Shell } from './components/Shell.js';
import { ToastProvider } from './ui/Toast.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { useUI } from './stores/ui.js';

function lazyNamed<TModule, TKey extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TKey,
) {
  return lazy(async () => ({
    default: (await loader())[exportName] as ComponentType,
  }));
}

const HomePage = lazyNamed(() => import('./pages/HomePage.js'), 'HomePage');
const WeekPage = lazyNamed(() => import('./pages/WeekPage.js'), 'WeekPage');
const AssignmentsPage = lazyNamed(() => import('./pages/AssignmentsPage.js'), 'AssignmentsPage');
const RolesPage = lazyNamed(() => import('./pages/roles/RolesPage.js'), 'RolesPage');
const DashboardPage = lazyNamed(() => import('./pages/DashboardPage.js'), 'DashboardPage');
const CashboxPage = lazyNamed(() => import('./pages/CashboxPage.js'), 'CashboxPage');
const CashDeskPage = lazyNamed(() => import('./pages/CashDeskPage.js'), 'CashDeskPage');
const ArchivePage = lazyNamed(() => import('./pages/ArchivePage.js'), 'ArchivePage');
const OrderDetailPage = lazyNamed(() => import('./pages/OrderDetailPage.js'), 'OrderDetailPage');
const ClientRequestsPausedPage = lazyNamed(() => import('./pages/ClientRequestsPausedPage.js'), 'ClientRequestsPausedPage');
const ClientTrackerPage = lazyNamed(() => import('./pages/ClientTrackerPage.js'), 'ClientTrackerPage');
const ClientReferralsPage = lazyNamed(() => import('./pages/ClientReferralsPage.js'), 'ClientReferralsPage');
const HandoffPage = lazyNamed(() => import('./pages/HandoffPage.js'), 'HandoffPage');
const PickupPage = lazyNamed(() => import('./pages/driver/PickupPage.js'), 'PickupPage');
const DeliverPage = lazyNamed(() => import('./pages/driver/DeliverPage.js'), 'DeliverPage');
const DriverSummaryPage = lazyNamed(() => import('./pages/driver/DriverSummaryPage.js'), 'DriverSummaryPage');
const CheckPage = lazyNamed(() => import('./pages/qc/CheckPage.js'), 'CheckPage');
const DefectsPage = lazyNamed(() => import('./pages/qc/DefectsPage.js'), 'DefectsPage');
const QcSummaryPage = lazyNamed(() => import('./pages/qc/QcSummaryPage.js'), 'QcSummaryPage');
const DispatchPage = lazyNamed(() => import('./pages/DispatchPage.js'), 'DispatchPage');
const InstallationDefectsPage = lazyNamed(() => import('./pages/installation/InstallationDefectsPage.js'), 'InstallationDefectsPage');
const InstallationQualityPage = lazyNamed(() => import('./pages/installation/InstallationQualityPage.js'), 'InstallationQualityPage');
const WarehouseFinishedPage = lazyNamed(() => import('./pages/warehouse/WarehouseFinishedPage.js'), 'WarehouseFinishedPage');
const ProductionOwnPage = lazyNamed(() => import('./pages/production/ProductionWorkQueuePage.js'), 'ProductionOwnPage');
const ProductionContractorsPage = lazyNamed(() => import('./pages/production/ProductionWorkQueuePage.js'), 'ProductionContractorsPage');
const DealerHomePage = lazyNamed(() => import('./pages/dealer/DealerHomePage.js'), 'DealerHomePage');
const DealerOrderPage = lazyNamed(() => import('./pages/dealer/DealerOrderPage.js'), 'DealerOrderPage');
const DealerCalcPage = lazyNamed(() => import('./pages/dealer/DealerCalcPage.js'), 'DealerCalcPage');
const ProfileSheet = lazyNamed(() => import('./components/ProfileSheet.js'), 'ProfileSheet');
const InboxSheet = lazyNamed(() => import('./components/InboxSheet.js'), 'InboxSheet');

function RouteLoading() {
  return (
    <div
      role="status"
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--text-muted)',
      }}
    >
      Загрузка…
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <Suspense fallback={<RouteLoading />}>
          <Routes>
            {/* Публичная страница — клиентский трекер заказа по shareToken.
                ВНЕ DeviceGate и AuthGate: открывается прямо из камеры iPhone
                без логина, должна работать на любом устройстве. */}
            <Route path="c/:token" element={<ClientTrackerPage />} />

            {/* Всё остальное — приватное, через AuthGate */}
            <Route path="*" element={<ProtectedApp />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function ProtectedApp() {
  return (
    <DeviceGate>
      <AuthGate>
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<HomePage />} />
            <Route path="week" element={<WeekPage />} />
            <Route path="assignments" element={<AssignmentsPage />} />
            {/* Окна сотрудников для администратора — вкладка «Роли». */}
            <Route path="assignments/measures" element={<Navigate to="/roles/measures" replace />} />
            <Route path="assignments/installs" element={<Navigate to="/roles/installs" replace />} />
            <Route path="roles" element={<Navigate to="/roles/measures" replace />} />
            <Route path="roles/:view" element={<RolesPage />} />
            <Route path="roles/:view/:section" element={<RolesPage />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="cashbox" element={<CashboxPage />} />
            <Route path="cashdesk" element={<CashDeskPage />} />
            <Route path="handoff" element={<HandoffPage />} />
            {/* Окно водителя — 3 вкладки */}
            <Route path="driver/pickup" element={<PickupPage />} />
            <Route path="driver/deliver" element={<DeliverPage />} />
            <Route path="driver/summary" element={<DriverSummaryPage />} />
            {/* Окно ОТК — 3 вкладки */}
            <Route path="qc/check" element={<CheckPage />} />
            <Route path="qc/defects" element={<DefectsPage />} />
            <Route path="qc/summary" element={<QcSummaryPage />} />
            <Route path="dispatch" element={<DispatchPage />} />
            <Route path="installation/defects" element={<InstallationDefectsPage />} />
            <Route path="installation/quality" element={<InstallationQualityPage />} />
            <Route path="warehouse/finished" element={<WarehouseFinishedPage />} />
            <Route path="warehouse/defects" element={<Navigate to="/warehouse/finished" replace />} />
            {/* Мобильное рабочее место производства — те же безопасные очереди,
                что в Admin, но в вертикальном Field-интерфейсе. */}
            <Route path="production/own" element={<ProductionOwnPage />} />
            <Route path="production/contractors" element={<ProductionContractorsPage />} />
            <Route path="catalog/*" element={<ClientRequestsPausedPage />} />
            <Route path="referrals" element={<ClientReferralsPage />} />
            <Route path="quotes/*" element={<ClientRequestsPausedPage />} />
            {/* Дилерский кабинет — отдельная главная для роли DEALER. */}
            <Route path="d" element={<DealerHomePage />} />
          </Route>
          <Route path="orders/:id" element={<OrderDetailWrapper />} />
          <Route path="d/orders/:id" element={<DealerOrderWrapper />} />
          <Route path="d/new-order" element={<DealerCalcWrapper />} />
          <Route path="archive" element={<ArchiveWrapper />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Глобальные bottom-sheet'ы — открываются из шапки */}
        <Suspense fallback={null}>
          <ProfileSheetHost />
          <InboxSheetHost />
        </Suspense>
      </AuthGate>
    </DeviceGate>
  );
}

function ProfileSheetHost() {
  const open = useUI((state) => state.profileSheetOpen);
  return open ? <ProfileSheet /> : null;
}

function InboxSheetHost() {
  const open = useUI((state) => state.inboxSheetOpen);
  return open ? <InboxSheet /> : null;
}

function OrderDetailWrapper() {
  // Внешний контейнер — flex-column на всю высоту, без собственного скролла.
  // OrderDetailPage внутри сам разделяет AppBar (фиксированный) и контент (скроллируемый).
  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <OrderDetailPage />
    </div>
  );
}

function DealerOrderWrapper() {
  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <DealerOrderPage />
    </div>
  );
}

function DealerCalcWrapper() {
  return (
    <div style={{ height: '100dvh', overflowY: 'auto', overflowX: 'hidden', overscrollBehavior: 'contain' }}>
      <DealerCalcPage />
    </div>
  );
}

function ArchiveWrapper() {
  return (
    <div
      style={{
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <ArchivePage />
    </div>
  );
}
